import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Shield, Eye, EyeOff, AlertCircle, CheckCircle2, X } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { api } from '@/lib/api'

interface OAuthConfig {
  google: boolean
  microsoft: boolean
}

const SSO_ERROR_MESSAGES: Record<string, string> = {
  state_mismatch:         'Sign-in was interrupted. Please try again.',
  provider_not_configured:'This sign-in method is not configured.',
  missing_code_or_state:  'Sign-in response was incomplete.',
  token_exchange_failed:  'Could not complete sign-in with the provider.',
  userinfo_failed:        'Could not read your profile from the provider.',
  network_error:          'Network error during sign-in. Check your connection.',
  missing_identity:       'Provider did not return your email address.',
  email_unverified:       'Please verify your email with the provider first.',
  not_on_waitlist:        "That email isn't on the beta list yet — register your interest below.",
  demo_user_sso_blocked:  'Demo accounts cannot use single sign-on.',
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, loading, error } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [showInterest, setShowInterest] = useState(false)
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const ssoError = searchParams.get('sso_error')

  useEffect(() => {
    // Discover which SSO providers are configured on this instance so we
    // can hide buttons that would 503.
    api.get('/api/auth/oauth/config')
      .then(r => setOauthConfig(r.data))
      .catch(() => setOauthConfig({ google: false, microsoft: false }))
  }, [])

  // Auto-open the register-interest modal when the user was bounced with
  // not_on_waitlist so they can sign up without hunting for the link
  useEffect(() => {
    if (ssoError === 'not_on_waitlist') {
      setShowInterest(true)
    }
  }, [ssoError])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await login(email, password)
      navigate('/mfa')
    } catch {
      // Error is already set in the auth store
    }
  }

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-xl bg-grad-blue flex items-center justify-center">
                <Shield size={28} className="text-white" aria-hidden />
              </div>
            </div>
            <h1 className="font-display text-2xl font-bold text-text-primary tracking-wide">AgentVault Capital</h1>
            <p className="text-text-secondary text-sm mt-1 tracking-widest uppercase">Private Fund Terminal</p>
            <p className="text-text-muted text-xs mt-3">Secure Terminal Access</p>
          </div>

          {/* Error */}
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-rose/10 border border-rose/20 text-rose-light text-sm"
            >
              <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden />
              {error}
            </div>
          )}

          {/* SSO error banner */}
          {ssoError && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-amber/10 border border-amber/20 text-amber text-sm"
            >
              <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden />
              <div className="flex-1">
                {SSO_ERROR_MESSAGES[ssoError] ?? `Sign-in failed: ${ssoError}`}
              </div>
              <button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  next.delete('sso_error')
                  setSearchParams(next, { replace: true })
                }}
                className="text-amber/60 hover:text-amber shrink-0"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* SSO buttons */}
          {(oauthConfig?.google || oauthConfig?.microsoft) && (
            <div className="space-y-2 mb-5">
              {oauthConfig?.google && (
                <a
                  href="/api/auth/oauth/google/start"
                  className="btn-secondary w-full py-3 flex items-center justify-center gap-3 text-sm"
                >
                  <GoogleLogo />
                  Sign in with Google
                </a>
              )}
              {oauthConfig?.microsoft && (
                <a
                  href="/api/auth/oauth/microsoft/start"
                  className="btn-secondary w-full py-3 flex items-center justify-center gap-3 text-sm"
                >
                  <MicrosoftLogo />
                  Sign in with Microsoft
                </a>
              )}
              <div className="flex items-center gap-3 pt-2">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[10px] uppercase tracking-wider text-text-muted">
                  or with password
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-1.5">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="unman.lai@caiborg.ai"
                className="input"
                aria-required="true"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-secondary mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="input pr-10"
                  aria-required="true"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email || !password}
              className="btn-primary w-full py-3 text-base"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden />
                  Authenticating…
                </span>
              ) : (
                'Secure Login'
              )}
            </button>
          </form>

          {/* Auth info */}
          <p className="mt-6 text-center text-xs text-text-muted">
            MFA-protected login with TOTP verification
          </p>

          {/* Register your interest */}
          <p className="mt-4 text-center text-xs text-text-muted">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={() => setShowInterest(true)}
              className="text-blue-light hover:text-blue underline underline-offset-2"
            >
              Register your interest
            </button>
          </p>

          {/* Trust badges */}
          <div className="mt-6 flex justify-center gap-5 text-xs text-text-muted border-t border-border pt-5">
            <span>256-bit SSL</span>
            <span>·</span>
            <span>MFA protected</span>
            <span>·</span>
            <span>FCA aligned</span>
          </div>
        </div>
      </div>
      {showInterest && <RegisterInterestModal onClose={() => setShowInterest(false)} />}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider logos (inline SVG — no extra deps)
// ─────────────────────────────────────────────────────────────────────────────

function GoogleLogo() {
  return (
    <svg width={16} height={16} viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.3 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.7 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.6l6.2 5.2C41.4 35.3 44 30.1 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  )
}

function MicrosoftLogo() {
  return (
    <svg width={16} height={16} viewBox="0 0 23 23" aria-hidden>
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Register Interest modal
// ─────────────────────────────────────────────────────────────────────────────

function RegisterInterestModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<
    { ok: boolean; text: string } | null
  >(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setResult(null)
    try {
      const { data } = await api.post('/api/auth/register-interest', {
        email: email.trim(),
        name:  name.trim(),
        company: company.trim() || null,
        message: message.trim() || null,
      })
      setResult({ ok: true, text: data.detail || 'Thanks — we\'ll be in touch.' })
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Could not submit — please try again.'
      setResult({ ok: false, text: detail })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="card p-6 w-full max-w-md relative"
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1 text-text-muted hover:text-text-primary"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <h2 className="text-lg font-bold text-text-primary mb-1">Register your interest</h2>
        <p className="text-xs text-text-muted mb-5">
          We're inviting a small group of beta testers. Leave your details and
          we'll get in touch when a slot opens up.
        </p>

        {result?.ok ? (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald/10 border border-emerald/30 text-emerald text-sm">
            <CheckCircle2 size={16} className="shrink-0 mt-0.5" aria-hidden />
            <span>{result.text}</span>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" noValidate>
            {result && !result.ok && (
              <div
                role="alert"
                className="flex items-start gap-2 p-2 rounded bg-rose/10 border border-rose/20 text-rose-light text-xs"
              >
                <AlertCircle size={14} className="shrink-0 mt-0.5" aria-hidden />
                {result.text}
              </div>
            )}

            <div>
              <label htmlFor="ri-name" className="block text-xs font-medium text-text-secondary mb-1">Name</label>
              <input
                id="ri-name"
                type="text"
                required
                value={name}
                onChange={e => setName(e.target.value)}
                className="input"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label htmlFor="ri-email" className="block text-xs font-medium text-text-secondary mb-1">Email</label>
              <input
                id="ri-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="ri-company" className="block text-xs font-medium text-text-secondary mb-1">Company (optional)</label>
              <input
                id="ri-company"
                type="text"
                value={company}
                onChange={e => setCompany(e.target.value)}
                className="input"
                placeholder="Example Wealth Ltd"
              />
            </div>

            <div>
              <label htmlFor="ri-message" className="block text-xs font-medium text-text-secondary mb-1">
                Why are you interested? (optional)
              </label>
              <textarea
                id="ri-message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                className="input min-h-[80px] resize-y"
                placeholder="What would you use it for?"
                maxLength={500}
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !email || !name}
              className="btn-primary w-full"
            >
              {submitting ? 'Submitting…' : 'Register your interest'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
